import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import NotFound from "../pages/NotFound";

/* ─── PUBLIC / MARKETING PAGES (lazy-loaded) ─── */
const Home = lazy(() => import("../pages/home/page"));
const AuthPage = lazy(() => import("../pages/auth/page"));
const ResetPasswordPage = lazy(() => import("../pages/auth/reset/page"));
const VerifyEmailPage = lazy(() => import("../pages/auth/verify/page"));
const OnboardingPage = lazy(() => import("../pages/onboarding/page"));
const PricingPage = lazy(() => import("../pages/pricing/page"));
const FeaturesPage = lazy(() => import("../pages/features/page"));
const DemoPage = lazy(() => import("../pages/demo/page"));
const AboutPage = lazy(() => import("../pages/about/page"));
const SupportPage = lazy(() => import("../pages/support/page"));
const HelpPage = lazy(() => import("../pages/help/page"));
const HelpArticlePage = lazy(() => import("../pages/help/article/page"));
const BlogPage = lazy(() => import("../pages/blog/page"));
const BlogArticlePage = lazy(() => import("../pages/blog/article/page"));
const PublicReviewPage = lazy(() => import("../pages/review/page"));

/* ─── STUDIO LAYOUT (lazy — defers supabase chunk until /studio routes) ─── */
const StudioLayout = lazy(() => import("../pages/studio/StudioLayout"));

/* ─── STUDIO PAGES (code-split) ─── */
const StudioDashboard = lazy(() => import("../pages/studio/dashboard/page"));
const StudioEditor = lazy(() => import("../pages/studio/editor/page"));
const StudioLibrary = lazy(() => import("../pages/studio/library/page"));
const StudioCampaigns = lazy(() => import("../pages/studio/campaigns/page"));
const CampaignDetail = lazy(() => import("../pages/studio/campaigns/detail/page"));
const BrandConfigPage = lazy(() => import("../pages/studio/brand-config/page"));
const OrganizationsPage = lazy(() => import("../pages/studio/organizations/page"));
const InviteAcceptancePage = lazy(() => import("../pages/studio/invite/page"));
const StudioApprovals = lazy(() => import("../pages/studio/approvals/page"));
const ApprovalDetail = lazy(() => import("../pages/studio/approvals/detail/page"));
const NotificationSettingsPage = lazy(() => import("../pages/studio/notifications/page"));
const ProfilePage = lazy(() => import("../pages/studio/profile/page"));
const OrderHistoryPage = lazy(() => import("../pages/studio/orders/page"));
const StudioAssetManager = lazy(() => import("../pages/studio/assets/page"));
const StudioUpgradePage = lazy(() => import("../pages/studio/upgrade/page"));
const TemplateDirectoryPage = lazy(() => import("../pages/studio/templates/page"));
const BrandSourcesPage = lazy(() => import("../pages/studio/brand-sources/page"));
const SourcePackBuilderPage = lazy(() => import("../pages/studio/source-packs/builder/page"));
const SourcePackLibraryPage = lazy(() => import("../pages/studio/source-packs/library/page"));
const CheckoutPage = lazy(() => import("../pages/studio/checkout/page"));
const CheckoutSuccessPage = lazy(() => import("../pages/studio/checkout/success/page"));
const StudioAnalyticsPage = lazy(() => import("../pages/studio/analytics/page"));
const PipelinePage = lazy(() => import("../pages/studio/pipeline/page"));
const WorkflowsPage = lazy(() => import("../pages/studio/workflows/page"));
const BriefsPage = lazy(() => import("../pages/studio/briefs/page"));
const CalendarPage = lazy(() => import("../pages/studio/calendar/page"));
const PublishConnectorsPage = lazy(() => import("../pages/studio/publish-connectors/page"));

/* ─── ADMIN CONSOLE (code-split) ─── */
const AdminOverviewPage = lazy(() => import("../pages/studio/admin/page"));
const AdminOrdersPage = lazy(() => import("../pages/studio/admin/orders/page"));
const SpecimenReviewPage = lazy(() => import("../pages/studio/admin/design-library/page"));
const LibraryVersionsPage = lazy(() => import("../pages/studio/admin/library-versions/page"));
const DisciplinesPage = lazy(() => import("../pages/studio/admin/disciplines/page"));
const TestBenchPage = lazy(() => import("../pages/studio/admin/test-bench/page"));
const RuntimeRolloutPage = lazy(() => import("../pages/studio/admin/runtime-rollout/page"));
const DecommissionPage = lazy(() => import("../pages/studio/admin/decommission/page"));
const LearningLoopPage = lazy(() => import("../pages/studio/admin/learning-loop/page"));
const SystemReadinessPage = lazy(() => import("../pages/studio/admin/system-readiness/page"));
const AdminSettingsPage = lazy(() => import("../pages/studio/admin/settings/page"));
const CMSAdminPage = lazy(() => import("../pages/studio/admin/cms/page"));

const routes: RouteObject[] = [
  { path: "/", element: <Home /> },
  { path: "/auth", element: <AuthPage /> },
  { path: "/auth/reset", element: <ResetPasswordPage /> },
  { path: "/auth/verify", element: <VerifyEmailPage /> },
  { path: "/onboarding", element: <OnboardingPage /> },
  { path: "/pricing", element: <PricingPage /> },
  { path: "/features", element: <FeaturesPage /> },
  { path: "/demo", element: <DemoPage /> },
  { path: "/about", element: <AboutPage /> },
  { path: "/support", element: <SupportPage /> },
  { path: "/help", element: <HelpPage /> },
  { path: "/help/:slug", element: <HelpArticlePage /> },
  { path: "/blog", element: <BlogPage /> },
  { path: "/blog/:slug", element: <BlogArticlePage /> },
  { path: "/review", element: <PublicReviewPage /> },
  {
    path: "/studio",
    element: <StudioLayout />,
    children: [
      { index: true, element: <StudioDashboard /> },
      { path: "editor", element: <StudioEditor /> },
      { path: "library", element: <StudioLibrary /> },
      { path: "campaigns", element: <StudioCampaigns /> },
      { path: "campaigns/:id", element: <CampaignDetail /> },
      { path: "brand-config", element: <BrandConfigPage /> },
      { path: "organizations", element: <OrganizationsPage /> },
      { path: "notifications", element: <NotificationSettingsPage /> },
      { path: "profile", element: <ProfilePage /> },
      { path: "assets", element: <StudioAssetManager /> },
      { path: "upgrade", element: <StudioUpgradePage /> },
      { path: "orders", element: <OrderHistoryPage /> },
      { path: "checkout", element: <CheckoutPage /> },
      { path: "checkout/success", element: <CheckoutSuccessPage /> },
      { path: "templates", element: <TemplateDirectoryPage /> },
      { path: "brand-sources", element: <BrandSourcesPage /> },
      { path: "source-packs/builder", element: <SourcePackBuilderPage /> },
      { path: "source-packs/library", element: <SourcePackLibraryPage /> },
      { path: "analytics", element: <StudioAnalyticsPage /> },
      { path: "pipeline", element: <PipelinePage /> },
      { path: "workflows", element: <WorkflowsPage /> },
      { path: "briefs", element: <BriefsPage /> },
      { path: "briefs/:id", element: <BriefsPage /> },
      { path: "calendar", element: <CalendarPage /> },
      { path: "publish-connectors", element: <PublishConnectorsPage /> },
      { path: "approvals", element: <StudioApprovals /> },
      { path: "approvals/:id", element: <ApprovalDetail /> },
      // Admin Console
      { path: "admin", element: <AdminOverviewPage /> },
      { path: "admin/orders", element: <AdminOrdersPage /> },
      { path: "admin/design-library", element: <SpecimenReviewPage /> },
      { path: "admin/library-versions", element: <LibraryVersionsPage /> },
      { path: "admin/disciplines", element: <DisciplinesPage /> },
      { path: "admin/test-bench", element: <TestBenchPage /> },
      { path: "admin/runtime-rollout", element: <RuntimeRolloutPage /> },
      { path: "admin/decommission", element: <DecommissionPage /> },
      { path: "admin/learning-loop", element: <LearningLoopPage /> },
      { path: "admin/system-readiness", element: <SystemReadinessPage /> },
      { path: "admin/settings", element: <AdminSettingsPage /> },
      { path: "admin/cms", element: <CMSAdminPage /> },
    ],
  },
  { path: "/studio/invite", element: <InviteAcceptancePage /> },
  { path: "*", element: <NotFound /> },
];

export default routes;